import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { SupportService } from "src/app/shared/services/support/support.service";
import { SupportSocketService } from "src/app/shared/services/support/support-socket.service";
import { UserDetailsService } from "src/app/shared/services/user-details.service";

export type SupportScreen =
  | "home"
  | "chatForm"
  | "callback"
  | "chat"
  | "success";

type SupportChatItem =
  | {
      kind: "divider";
      label: string;
      sublabel?: string;
    }
  | {
      kind: "date-divider";
      label: string;
    }
  | {
      kind: "system-divider";
      id: string;
      label: string;
    }
  | {
      kind: "message";
      id?: string;
      text: string;
      who: "me" | "agent";
      time: string;
      attachments?: any[];
      raw?: any;
    };

type SupportReplyTarget = {
  id: string;
  text: string;
  who: "me" | "agent";
  attachments?: any[];
  raw?: any;
};

export class SupportChatFacade {
  private static readonly MAX_SUPPORT_ATTACHMENTS = 4;
  private static readonly MAX_SUPPORT_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
  private static readonly MAX_SUPPORT_VIDEO_ATTACHMENT_SIZE_BYTES = 15 * 1024 * 1024;
  private static readonly MAX_SUPPORT_TEXT_LENGTH = 800;
  private static readonly MAX_SUPPORT_PARAGRAPHS = 2;
  private static readonly SUPPORT_ATTACHMENT_ACCEPTED_EXTENSIONS = new Set([
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".bmp",
    ".heic",
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".txt",
    ".csv",
    ".mp4",
    ".mov",
    ".avi",
    ".mkv",
    ".webm",
    ".m4v",
  ]);
  private static readonly SUPPORT_ATTACHMENT_ACCEPTED_MIME_TYPES = new Set([
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "text/csv",
    "video/mp4",
    "video/quicktime",
    "video/x-msvideo",
    "video/x-matroska",
    "video/webm",
    "video/x-m4v",
  ]);
  isSupportShellOpen = false;
  currentSupportScreen: SupportScreen = "home";
  supportCategoryOptions = [
    "Technical issue",
    "Account & login",
    "Billing & subscription",
    "Feature guidance",
    "Other",
  ];
  supportSelectFocus = {
    chatCategory: false,
    callbackReason: false,
  };
  selectedSupportCategory = "";
  selectedCallbackReason = "";
  supportGreetingName = "there";
  callbackName = "";
  callbackIsdCode = "+91";
  callbackMobileNumber = "";
  protected loggedInUserName = "";
  protected loggedInUserMobileNumber = "";
  loggedInUserIsdCode = "+91";
  callbackMessage = "";
  supportChatMessage = "";
  supportReplyMessage = "";
  supportEmojiOptions = ["😀", "😂", "😊", "😍", "👍", "🙏", "🎉", "😔", "👀", "✅"];
  showSupportEmojiPicker = false;
  pendingSupportAttachments: Array<{
    name: string;
    size: number;
    file: File;
    previewUrl: string | null;
    isImage: boolean;
    isVideo?: boolean;
    previewFailed?: boolean;
  }> = [];
  supportThreadData: any = null;
  activeSupportIssueSession: any = null;
  supportDiscussionId = "";
  supportUserId = 0;
  supportLoadedIssues: any[] = [];
  supportHasMoreIssueHistory = false;
  supportNextHistoryCursor: string | null = null;
  supportLoadingOlderIssueHistory = false;
  isOpeningSupportShell = false;
  isSubmittingSupportChat = false;
  isSubmittingCallbackRequest = false;
  isSendingSupportMessage = false;
  supportUnreadCount = 0;
  chatMessages: SupportChatItem[] = [];
  showSupportTyping = false;
  supportTypingLabel = "";
  supportReplyTarget: SupportReplyTarget | null = null;
  supportMessageMenu = {
    open: false,
    x: 0,
    y: 0,
    message: null as SupportReplyTarget | null,
  };
  private supportJoinedIssueIds = new Set<string>();
  protected supportTypingChanges$ = new Subject<string | null>();
  private activeSupportRoomId = "";
  private supportBottomScrollFrameId: number | null = null;
  private supportBottomScrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    protected readonly userDetailsService: UserDetailsService,
    protected readonly supportService: SupportService,
    protected readonly toastrService: ToastrService,
    protected readonly supportSocketService: SupportSocketService
  ) {
    this.supportTypingChanges$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((message) => {
        this.supportSocketService.sendTyping(message);
      });

    this.supportSocketService.newMessage$.subscribe((message) => {
      this.handleIncomingSupportMessage(message);
    });

    this.supportSocketService.typingUsers$.subscribe((users: any[]) => {
      const typingUsers = users.filter(
        (item) => Number(item?.user_id || 0) !== this.supportUserId
      );
      if (typingUsers.length) {
        this.showSupportJoinedNoticeIfNeeded();
      }
      this.showSupportTyping = typingUsers.length > 0;
      this.supportTypingLabel = typingUsers.length
        ? this.getSupportTypingLabel()
        : "";
    });
  }

  async initializeSupportWidget() {
    await this.prefillSupportUserDetails();
    await this.bootstrapActiveSupportNotifications();
  }

  destroySupportWidget() {
    this.leaveActiveSupportRoom();
    this.clearPendingSupportAttachments();
  }

  toggleSupportShell(event?: Event) {
    event?.stopPropagation();
    if (this.isSupportShellOpen) {
      this.closeSupportShell();
      return;
    }

    if (this.hasActiveSupportIssue()) {
      this.currentSupportScreen = "chat";
    }

    this.isSupportShellOpen = true;
    void this.initializeSupportShell();
  }

  closeSupportShell() {
    const preserveActiveSession = this.hasActiveSupportIssue();
    if (!preserveActiveSession) {
      this.leaveActiveSupportRoom();
    }
    this.isSupportShellOpen = false;
    this.resetSupportShell(preserveActiveSession);
  }

  preventSupportShellClose(event: Event) {
    event.stopPropagation();
  }

  openSupportScreen(screen: SupportScreen, event?: Event) {
    event?.stopPropagation();
    if (screen === "chatForm") {
      void this.openSupportChatEntry();
      return;
    }
    this.currentSupportScreen = screen;
  }

  toggleSupportEmojiPicker(event?: Event) {
    event?.stopPropagation();
    this.showSupportEmojiPicker = !this.showSupportEmojiPicker;
  }

  addSupportEmoji(emoji: string, event?: Event) {
    event?.stopPropagation();
    this.supportReplyMessage = this.normalizeSupportTextInput(
      `${this.supportReplyMessage}${emoji}`
    );
    this.showSupportEmojiPicker = false;
  }

  onSupportChatMessageChange(value: string) {
    this.supportChatMessage = this.normalizeSupportTextInput(value);
  }

  onCallbackMessageChange(value: string) {
    this.callbackMessage = this.normalizeSupportTextInput(value);
  }

  onCallbackMobileNumberChange(value: string) {
    this.callbackMobileNumber = this.normalizeCallbackMobileNumber(value);
  }

  async onSupportAttachmentSelected(fileList: FileList | null) {
    if (!fileList?.length) {
      return;
    }

    this.appendPendingSupportAttachments(Array.from(fileList));
  }

  onSupportComposerPaste(event: ClipboardEvent) {
    const clipboardItems = Array.from(event.clipboardData?.items || []);
    if (!clipboardItems.length) {
      return;
    }

    const pastedFiles = clipboardItems
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file);

    if (!pastedFiles.length) {
      return;
    }

    event.preventDefault();
    this.appendPendingSupportAttachments(pastedFiles);
  }

  private appendPendingSupportAttachments(files: File[]) {
    if (!files.length) {
      return;
    }

    const remainingSlots =
      SupportChatFacade.MAX_SUPPORT_ATTACHMENTS -
      this.pendingSupportAttachments.length;

    if (remainingSlots <= 0) {
      this.toastrService.error("You can attach up to 4 files only.");
      return;
    }

    const nextFiles: Array<{
      name: string;
      size: number;
      file: File;
      previewUrl: string | null;
      isImage: boolean;
      isVideo?: boolean;
      previewFailed?: boolean;
    }> = [];
    let exceededLimit = false;

    files.forEach((file) => {
      const validationError = this.getSupportAttachmentValidationError(file);
      if (validationError) {
        this.toastrService.error(validationError);
        return;
      }

      if (nextFiles.length >= remainingSlots) {
        exceededLimit = true;
        return;
      }

      const lowerName = String(file?.name || "").toLowerCase();
      const isImage =
        file.type.startsWith("image/") ||
        [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".heic"].some(
          (ext) => lowerName.endsWith(ext)
        );
      const isVideo =
        file.type.startsWith("video/") ||
        [".mp4", ".mov", ".avi", ".mkv", ".webm", ".m4v"].some((ext) =>
          lowerName.endsWith(ext)
        );

      nextFiles.push({
        name: file.name,
        size: file.size,
        file,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
        isImage,
        isVideo,
        previewFailed: false,
      });
    });

    if (!nextFiles.length) {
      if (exceededLimit) {
        this.toastrService.error("You can attach up to 4 files only.");
      }
      return;
    }

    this.pendingSupportAttachments = [
      ...this.pendingSupportAttachments,
      ...nextFiles,
    ];

    if (exceededLimit) {
      this.toastrService.error("You can attach up to 4 files only.");
    }
  }

  private getSupportAttachmentValidationError(file: File) {
    const fileName = String(file?.name || "file");
    const lowerName = fileName.toLowerCase();
    const extension = lowerName.includes(".")
      ? lowerName.slice(lowerName.lastIndexOf("."))
      : "";
    const mimeType = String(file?.type || "").toLowerCase();
    const isAllowed =
      mimeType.startsWith("image/") ||
      SupportChatFacade.SUPPORT_ATTACHMENT_ACCEPTED_MIME_TYPES.has(mimeType) ||
      SupportChatFacade.SUPPORT_ATTACHMENT_ACCEPTED_EXTENSIONS.has(extension);

    if (!isAllowed) {
      return `${fileName}: only images, video, PDF, Word, Excel, TXT, and CSV files are allowed.`;
    }

    const isVideo =
      mimeType.startsWith("video/") ||
      [".mp4", ".mov", ".avi", ".mkv", ".webm", ".m4v"].some((ext) =>
        lowerName.endsWith(ext)
      );
    const maxAllowedSize = isVideo
      ? SupportChatFacade.MAX_SUPPORT_VIDEO_ATTACHMENT_SIZE_BYTES
      : SupportChatFacade.MAX_SUPPORT_ATTACHMENT_SIZE_BYTES;

    if (Number(file?.size || 0) > maxAllowedSize) {
      return isVideo
        ? `${fileName}: video size must be 15 MB or less.`
        : `${fileName}: file size must be 5 MB or less.`;
    }

    return "";
  }

  removePendingSupportAttachment(index: number, event?: Event) {
    event?.stopPropagation();
    const item = this.pendingSupportAttachments[index];
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
    this.pendingSupportAttachments = this.pendingSupportAttachments.filter(
      (_attachment, itemIndex) => itemIndex !== index
    );
  }

  setSupportSelectFocus(
    field: keyof typeof this.supportSelectFocus,
    focused: boolean
  ) {
    this.supportSelectFocus[field] = focused;
  }

  async startSupportChat(message: string, event?: Event) {
    event?.stopPropagation();
    const trimmedMessage = message.trim();

    if (
      !trimmedMessage ||
      trimmedMessage.length < 10 ||
      !this.selectedSupportCategory ||
      this.isSubmittingSupportChat
    ) {
      return;
    }

    this.isSubmittingSupportChat = true;
    try {
      const response: any = await this.supportService.startChatSession({
        support_category: this.selectedSupportCategory,
        issue_summary: trimmedMessage,
      });

      if (response === 400) {
        return;
      }

      const result = response?.result;
      if (!result?.active_issue_session) {
        this.toastrService.error("Unable to start support chat.");
        return;
      }

      this.supportThreadData = result.thread || null;
      this.activeSupportIssueSession = result.active_issue_session;
      this.supportDiscussionId =
        result?.active_issue_session?.discussion_id ||
        result?.thread?.discussion_id ||
        "";
      this.supportChatMessage = "";
      this.supportReplyMessage = "";
      this.currentSupportScreen = "chat";
      this.clearSupportUnreadCount();
      if (!this.applySupportChatState(result?.chat_state || result)) {
        await this.loadSupportDiscussionHistory(
          this.activeSupportIssueSession,
          this.supportDiscussionId
        );
      }
      this.bindSupportRoom();

      if (result?.created_new_issue === false) {
        this.toastrService.info("Opened your existing support chat.");
      }
    } finally {
      this.isSubmittingSupportChat = false;
    }
  }

  async sendSupportMessage(event?: Event) {
    event?.stopPropagation();
    const rawValue = this.supportReplyMessage;
    const value = rawValue.trim();
    if (
      !this.supportDiscussionId ||
      !this.activeSupportIssueSession?._id ||
      (!value && !this.pendingSupportAttachments.length) ||
      this.isSendingSupportMessage
    ) {
      return;
    }

    this.isSendingSupportMessage = true;
    try {
      const pendingAttachmentSnapshot = this.pendingSupportAttachments.map(
        (item) => ({
          ...item,
        })
      );
      const parentId =
        this.supportReplyTarget?.id || this.supportReplyTarget?.raw?._id || "";
      const uploadFile = this.pendingSupportAttachments.length
        ? await Promise.all(
            this.pendingSupportAttachments.map((item) =>
              this.supportService.uploadSupportAttachment(
                item.file,
                this.supportDiscussionId
              )
            )
          )
        : [];
      const response: any = await this.supportSocketService.sendMessage({
          discussionId: this.supportDiscussionId,
          message: rawValue,
          uploadFile,
          support_thread_id: String(this.supportThreadData?._id || ""),
          support_issue_session_id: String(
            this.activeSupportIssueSession?._id || ""
          ),
          ...(parentId ? { parent_id: parentId } : {}),
        });

      if (response === 400 || !response?.success) {
        return;
      }

      const result = response?.data || response?.result || response || {};
      this.supportReplyMessage = "";
      this.supportReplyTarget = null;
      this.supportTypingChanges$.next(null);
      this.showSupportEmojiPicker = false;

      const savedMessage =
        result && typeof result === "object" && result?._id
          ? result
          : result?.message &&
              typeof result.message === "object" &&
              result.message?._id
            ? result.message
            : null;

      if (savedMessage) {
        const savedMessageForDisplay = {
          ...savedMessage,
          attachement_details: Array.isArray(savedMessage?.attachement_details) &&
            savedMessage.attachement_details.length
            ? savedMessage.attachement_details
            : Array.isArray(savedMessage?.attachment_details) &&
                savedMessage.attachment_details.length
              ? savedMessage.attachment_details
              : Array.isArray(savedMessage?.uploadFile) &&
                  savedMessage.uploadFile.length
                ? savedMessage.uploadFile
                : uploadFile,
        };
        const mappedSavedMessage = this.mapSupportMessage(savedMessageForDisplay);
        const shouldHydrateSentAttachmentMessage =
          pendingAttachmentSnapshot.length > 0 ||
          uploadFile.length > 0 ||
          (Array.isArray(mappedSavedMessage?.attachments) &&
            mappedSavedMessage.attachments.length > 0);
        this.chatMessages = [
          ...this.chatMessages,
          mappedSavedMessage,
        ];
        this.clearPendingSupportAttachments();
        this.scrollSupportChatToBottom();
        if (shouldHydrateSentAttachmentMessage) {
          await this.hydrateSentSupportAttachmentMessage(
            String(savedMessage?._id || ""),
            this.activeSupportIssueSession,
            this.supportDiscussionId
          );
        }
      } else {
        this.clearPendingSupportAttachments();
        await this.loadSupportDiscussionHistory(
          this.activeSupportIssueSession,
          this.supportDiscussionId
        );
      }
      this.focusSupportReplyComposer();
    } finally {
      this.isSendingSupportMessage = false;
      this.focusSupportReplyComposer();
    }
  }

  onSupportComposerEnter(event: KeyboardEvent) {
    if (event.shiftKey) {
      return;
    }
    event.preventDefault();
    void this.sendSupportMessage(event);
  }

  async submitCallbackRequest(event?: Event) {
    event?.stopPropagation();
    const requestName = this.callbackName.trim() || this.loggedInUserName;
    const requestMobileNumber = this.getNormalizedCallbackRequestMobileNumber();

    if (
      !requestName ||
      !this.isCallbackMobileNumberValid() ||
      !this.selectedCallbackReason ||
      this.callbackMessage.trim().length < 10 ||
      this.isSubmittingCallbackRequest
    ) {
      if (!this.isSubmittingCallbackRequest && !this.isCallbackMobileNumberValid()) {
        this.toastrService.error("Enter a valid mobile number.");
      }
      return;
    }

    this.isSubmittingCallbackRequest = true;
    try {
      const response: any = await this.supportService.createCallRequest({
        support_category: this.selectedCallbackReason,
        issue_summary:
          this.callbackMessage.trim() ||
          `Callback requested for ${this.selectedCallbackReason}`,
        name: requestName,
        mobile_number: requestMobileNumber,
        isd_code: this.getNormalizedCallbackIsdCode(),
      });

      if (response === 400) {
        return;
      }

      this.selectedCallbackReason = "";
      this.callbackMessage = "";
      this.supportSelectFocus.callbackReason = false;
      this.currentSupportScreen = "success";
      if (response?.result?.user_notice) {
        this.toastrService.success(response.result.user_notice);
      }
    } finally {
      this.isSubmittingCallbackRequest = false;
    }
  }

  isCallbackMobileNumberValid() {
    const normalizedMobileNumber = this.getNormalizedCallbackRequestMobileNumber();
    return (
      normalizedMobileNumber.length >= 8 &&
      normalizedMobileNumber.length <= 15
    );
  }

  getCallbackMobileNumberValidationMessage() {
    const rawMobileNumber = String(this.callbackMobileNumber || "").trim();
    const normalizedMobileNumber = this.getNormalizedCallbackRequestMobileNumber();

    if (!rawMobileNumber) {
      return "Mobile number is required.";
    }

    if (normalizedMobileNumber.length < 8 || normalizedMobileNumber.length > 15) {
      return "Enter a valid mobile number with 8 to 15 digits.";
    }

    return "";
  }

  onCallbackIsdCodeChange(value: string) {
    const digits = String(value || "")
      .replace(/\D/g, "")
      .slice(0, 4);

    this.callbackIsdCode = digits ? `+${digits}` : "+";
  }

  getNormalizedCallbackIsdCode() {
    return this.normalizeSupportIsdCode(
      this.callbackIsdCode.trim() || this.loggedInUserIsdCode
    );
  }

  private getNormalizedCallbackRequestMobileNumber() {
    return this.normalizeCallbackMobileNumber(
      this.callbackMobileNumber.trim() || this.loggedInUserMobileNumber
    );
  }

  private normalizeCallbackMobileNumber(value: string) {
    return String(value || "")
      .replace(/\D/g, "")
      .slice(0, 15);
  }

  protected resetSupportShell(preserveActiveSession = false) {
    this.currentSupportScreen = "home";
    this.chatMessages = [];
    this.showSupportTyping = false;
    this.supportTypingLabel = "";
    this.supportSelectFocus = {
      chatCategory: false,
      callbackReason: false,
    };
    this.selectedSupportCategory = "";
    this.selectedCallbackReason = "";
    this.callbackMessage = "";
    this.supportChatMessage = "";
    this.supportReplyMessage = "";
    this.showSupportEmojiPicker = false;
    this.clearPendingSupportAttachments();
    if (!preserveActiveSession) {
      this.supportThreadData = null;
      this.activeSupportIssueSession = null;
      this.supportDiscussionId = "";
      this.supportLoadedIssues = [];
      this.supportHasMoreIssueHistory = false;
      this.supportNextHistoryCursor = null;
      this.supportJoinedIssueIds.clear();
    }
    this.supportLoadingOlderIssueHistory = false;
    this.isOpeningSupportShell = false;
    this.isSubmittingSupportChat = false;
    this.isSubmittingCallbackRequest = false;
    this.isSendingSupportMessage = false;
  }

  protected async initializeSupportShell() {
    if (this.isOpeningSupportShell) {
      return;
    }

    this.isOpeningSupportShell = true;
    try {
      const response: any = await this.supportService.getMySupportThreadState();
      const didOpenActiveChat = await this.tryOpenActiveSupportChat(response);
      if (!didOpenActiveChat) {
        this.currentSupportScreen = "home";
      }
    } catch (_error) {
      this.currentSupportScreen = "home";
    } finally {
      this.isOpeningSupportShell = false;
    }
  }

  protected async openSupportChatEntry() {
    if (this.isOpeningSupportShell) {
      return;
    }

    this.isOpeningSupportShell = true;
    try {
      const response: any = await this.supportService.getMySupportThreadState();
      const didOpenActiveChat = await this.tryOpenActiveSupportChat(response);
      if (!didOpenActiveChat) {
        this.currentSupportScreen = "chatForm";
      }
    } catch (_error) {
      this.currentSupportScreen = "chatForm";
    } finally {
      this.isOpeningSupportShell = false;
    }
  }

  protected async prefillSupportUserDetails() {
    const loginUserData = await this.userDetailsService.getLoginUserData();
    if (!loginUserData) {
      return;
    }
    const fullName = (loginUserData.user_name || "").trim();
    const mobileNumber = `${loginUserData.mobile_number || ""}`.trim();
    const isdCode = this.normalizeSupportIsdCode(loginUserData.isd_code);

    this.supportUserId = Number(loginUserData.user_id || loginUserData.id || 0);
    this.loggedInUserName = fullName;
    this.loggedInUserIsdCode = isdCode;
    this.loggedInUserMobileNumber =
      mobileNumber && mobileNumber !== "0" ? mobileNumber : "";
    this.supportGreetingName = fullName || "there";
    this.callbackName = fullName;
    this.callbackIsdCode = isdCode;
    this.callbackMobileNumber = this.loggedInUserMobileNumber;
  }

  protected normalizeSupportIsdCode(value: string) {
    const digits = String(value || "")
      .replace(/\D/g, "")
      .trim();

    return digits ? `+${digits}` : "+91";
  }

  protected async bootstrapActiveSupportNotifications() {
    try {
      const response: any = await this.supportService.getMySupportThreadState();
      const state = this.extractSupportChatState(response);
      const thread = state?.thread || null;
      const activeIssue =
        state?.active_issue_session ||
        this.getActiveSupportIssue(thread?.issues || []);

      if (!thread || !activeIssue) {
        return;
      }

      this.supportThreadData = thread;
      this.activeSupportIssueSession = activeIssue;
      this.supportDiscussionId =
        activeIssue?.discussion_id || thread?.discussion_id || "";
      this.supportUnreadCount = this.getSupportUnreadCountFromIssue(activeIssue);
      this.bindSupportRoom();
    } catch (_error) {
      // Keep support socket inactive when there is no recoverable active session.
    }
  }

  protected async loadSupportDiscussionHistory(
    issueSession?: any,
    discussionId?: string
  ) {
    const issueSummary = String(issueSession?.issue_summary || "").trim();
    const requestedAt = issueSession?.requested_at;

    if (!discussionId) {
      this.chatMessages = issueSummary
        ? [
            {
              kind: "message",
              text: issueSummary,
              who: "me",
              time: this.getSupportTime(requestedAt),
              attachments: [],
            },
          ]
        : [];
      this.scrollSupportChatToBottom();
      return;
    }

    try {
      const response: any = await this.supportService.getMySupportThreadState();
      if (!this.applySupportChatState(response)) {
        this.chatMessages = issueSummary
          ? [
              {
                kind: "message",
                text: issueSummary,
                who: "me",
                time: this.getSupportTime(requestedAt),
                attachments: [],
              },
            ]
          : [];
      }
    } catch (_error) {
      this.chatMessages = issueSummary
        ? [
            {
              kind: "message",
              text: issueSummary,
              who: "me",
              time: this.getSupportTime(requestedAt),
              attachments: [],
            },
          ]
        : [];
    }
    this.scrollSupportChatToBottom();
  }

  protected getActiveSupportIssue(issues: any[]) {
    if (!Array.isArray(issues) || !issues.length) {
      return null;
    }

    const activeStatuses = new Set(["requested", "assigned", "active"]);
    return (
      [...issues]
        .reverse()
        .find((issue: any) => activeStatuses.has(String(issue?.status || ""))) ||
      null
    );
  }

  protected async tryOpenActiveSupportChat(response: any) {
    if (response === 400) {
      return false;
    }

    const state = this.extractSupportChatState(response);
    const thread = state?.thread || null;
    const activeIssue =
      state?.active_issue_session ||
      this.getActiveSupportIssue(thread?.issues || []);

    if (!thread || !activeIssue) {
      return false;
    }

    this.supportThreadData = thread;
    this.activeSupportIssueSession = activeIssue;
    this.supportDiscussionId =
      activeIssue?.discussion_id || thread?.discussion_id || "";
    this.currentSupportScreen = "chat";
    this.clearSupportUnreadCount();
    if (!this.applySupportChatState(state)) {
      await this.loadSupportDiscussionHistory(
        this.activeSupportIssueSession,
        this.supportDiscussionId
      );
    }
    this.bindSupportRoom();
    return true;
  }

  protected extractSupportChatState(response: any) {
    const payload = response?.result || response?.data || response || null;
    if (!payload) {
      return null;
    }

    if (payload?.thread) {
      return payload;
    }

    return {
      thread: payload,
      active_issue_session: this.getActiveSupportIssue(payload?.issues || []),
      has_active_issue: !!this.getActiveSupportIssue(payload?.issues || []),
    };
  }

  protected applySupportChatState(response: any) {
    const state = this.extractSupportChatState(response);
    const thread = state?.thread || null;
    const issues = Array.isArray(thread?.issues) ? thread.issues : [];

    if (!thread || !issues.length) {
      return false;
    }

    this.supportThreadData = thread;
    this.activeSupportIssueSession =
      state?.active_issue_session ||
      this.getActiveSupportIssue(issues) ||
      this.activeSupportIssueSession;
    this.supportDiscussionId =
      this.activeSupportIssueSession?.discussion_id ||
      thread?.discussion_id ||
      this.supportDiscussionId;
    this.supportLoadedIssues = issues;
    this.supportHasMoreIssueHistory = !!state?.history_pagination?.has_more;
    this.supportNextHistoryCursor =
      state?.history_pagination?.next_cursor || null;

    const history = this.flattenSupportThreadHistory(this.supportLoadedIssues);
    if (!history.length) {
      return false;
    }

    this.chatMessages = history;
    if (this.currentSupportScreen === "chat") {
      this.clearSupportUnreadCount();
    }
    this.bindSupportRoom();
    this.scrollSupportChatToBottom();
    return true;
  }

  async loadOlderSupportHistory(scrollContainer?: HTMLElement | null) {
    if (
      this.supportLoadingOlderIssueHistory ||
      !this.supportHasMoreIssueHistory ||
      !this.supportNextHistoryCursor
    ) {
      return;
    }

    this.supportLoadingOlderIssueHistory = true;
    const previousScrollHeight = scrollContainer?.scrollHeight || 0;
    const previousScrollTop = scrollContainer?.scrollTop || 0;

    try {
      const response: any = await this.supportService.getMySupportThreadState(
        this.supportNextHistoryCursor
      );
      const state = this.extractSupportChatState(response);
      const thread = state?.thread || null;
      const olderIssues = Array.isArray(thread?.issues) ? thread.issues : [];

      if (thread) {
        this.supportThreadData = {
          ...thread,
          issues: [...olderIssues, ...this.supportLoadedIssues],
        };
      }

      if (olderIssues.length) {
        const existingIds = new Set(
          this.supportLoadedIssues.map((item: any) => String(item?._id || ""))
        );
        const uniqueOlderIssues = olderIssues.filter(
          (item: any) => !existingIds.has(String(item?._id || ""))
        );

        if (uniqueOlderIssues.length) {
          this.supportLoadedIssues = [
            ...uniqueOlderIssues,
            ...this.supportLoadedIssues,
          ];
          this.chatMessages = this.flattenSupportThreadHistory(
            this.supportLoadedIssues
          );
        }
      }

      this.supportHasMoreIssueHistory = !!state?.history_pagination?.has_more;
      this.supportNextHistoryCursor =
        state?.history_pagination?.next_cursor || null;

      requestAnimationFrame(() => {
        if (!scrollContainer) {
          return;
        }
        const nextScrollHeight = scrollContainer.scrollHeight;
        scrollContainer.scrollTop =
          nextScrollHeight - previousScrollHeight + previousScrollTop;
      });
    } finally {
      this.supportLoadingOlderIssueHistory = false;
    }
  }

  protected mapSupportMessage(item: any) {
    const senderId = Number(
      item?.sender_id || item?.sender?.user_id || item?.sender?.id || 0
    );
    const who: "me" | "agent" =
      senderId === this.supportUserId ? "me" : "agent";
    const messageTime =
      item?.created_at ||
      item?.createdAt ||
      item?.message?.created_at ||
      item?.message?.createdAt ||
      (item?.timestamp ? new Date(Number(item.timestamp) * 1000) : null);

    const attachments = Array.isArray(item?.attachement_details)
      ? item.attachement_details
      : Array.isArray(item?.attachment_details)
        ? item.attachment_details
        : Array.isArray(item?.attachment)
          ? item.attachment
          : Array.isArray(item?.uploadFile)
            ? item.uploadFile
            : [];

    return {
      kind: "message" as const,
      id: String(item?._id || ""),
      text: this.normalizeStoredSupportMessageText(item?.message),
      who,
      time: this.getSupportTime(messageTime),
      attachments: attachments.map((attachment: any) =>
        this.normalizeSupportAttachmentPayload(attachment)
      ),
      raw: item,
    };
  }

  openSupportMessageMenu(message: any, event: MouseEvent) {
    if (message?.kind !== "message") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.supportMessageMenu = {
      open: true,
      x: event.clientX,
      y: event.clientY,
      message: {
        id: String(message?.id || message?.raw?._id || ""),
        text: String(message?.text || ""),
        who: message?.who === "agent" ? "agent" : "me",
        attachments: Array.isArray(message?.attachments)
          ? message.attachments
          : [],
        raw: message?.raw || null,
      },
    };
  }

  closeSupportMessageMenu() {
    this.supportMessageMenu = {
      open: false,
      x: 0,
      y: 0,
      message: null,
    };
  }

  replyToSupportMessage(
    message?: SupportReplyTarget | null,
    event?: Event
  ) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!message) {
      return;
    }

    this.supportReplyTarget = message;
    this.closeSupportMessageMenu();
    this.focusSupportReplyComposer();
  }

  cancelSupportReply() {
    this.supportReplyTarget = null;
    this.focusSupportReplyComposer();
  }

  async copySupportMessage(
    message?: SupportReplyTarget | null,
    event?: Event
  ) {
    event?.preventDefault();
    event?.stopPropagation();
    const copyText = this.getSupportMessageCopyText(message);
    this.closeSupportMessageMenu();
    if (!copyText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(copyText);
      this.toastrService.success("Message copied");
    } catch (_error) {
      this.toastrService.error("Unable to copy message");
    }
  }

  getSupportMessageCopyText(message?: any) {
    const text = String(message?.text || message?.raw?.message || "").trim();
    if (text) {
      return text;
    }

    const attachments = Array.isArray(message?.attachments)
      ? message.attachments
      : Array.isArray(message?.raw?.attachement_details)
        ? message.raw.attachement_details
        : [];
    if (attachments.length) {
      return attachments
        .map(
          (attachment: any) =>
            attachment?.attachement_name ||
            attachment?.name ||
            attachment?.file_name ||
            "Attachment"
        )
        .join(", ");
    }

    return "";
  }

  canCopySupportMessage(message?: SupportReplyTarget | null) {
    return !!this.getSupportMessageCopyText(message);
  }

  getSupportReplySenderName(message?: any) {
    if (!message) {
      return "Message";
    }

    if (message?.who === "me") {
      return "You";
    }

    const raw = message?.raw || {};
    const fullName = [raw?.sender?.first_name, raw?.sender?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return fullName || "TaskOPad Support";
  }

  getSupportReplyPreviewText(message?: any) {
    const text = this.normalizeStoredSupportMessageText(
      message?.text || message?.raw?.message
    );
    if (text) {
      return text;
    }

    const attachments = Array.isArray(message?.attachments)
      ? message.attachments
      : Array.isArray(message?.raw?.attachement_details)
        ? message.raw.attachement_details
        : [];
    return attachments.length ? "Attachment" : "";
  }

  getSupportParentMessage(message?: any) {
    return message?.raw?.parent || null;
  }

  hasSupportParentMessage(message?: any) {
    return !!this.getSupportParentMessage(message)?._id;
  }

  getSupportParentSenderName(message?: any) {
    const parent = this.getSupportParentMessage(message);
    if (!parent) {
      return "";
    }

    const senderId = Number(
      parent?.sender_id || parent?.sender?.user_id || parent?.sender?.id || 0
    );
    if (senderId && senderId === this.supportUserId) {
      return "You";
    }

    const fullName = [parent?.sender?.first_name, parent?.sender?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return fullName || "TaskOPad Support";
  }

  getSupportParentPreviewText(message?: any) {
    const parent = this.getSupportParentMessage(message);
    if (!parent) {
      return "";
    }

    const text = this.normalizeStoredSupportMessageText(parent?.message);
    if (text) {
      return text;
    }

    const attachments = Array.isArray(parent?.attachement_details)
      ? parent.attachement_details
      : Array.isArray(parent?.attachment_details)
        ? parent.attachment_details
        : Array.isArray(parent?.attachment)
          ? parent.attachment
          : [];
    return attachments.length ? "Attachment" : "";
  }

  getSupportAttachmentUrl(attachment: any) {
    if (this.isSupportVideoAttachment(attachment)) {
      return this.appendSupportAttachmentRevision(
        this.getSupportAttachmentSourceUrl(attachment),
        attachment
      );
    }

    const rawUrl =
      this.getSupportAttachmentField(
        attachment,
        "attachement_url",
        "attachment_url"
      ) ||
      this.getSupportAttachmentField(
        attachment,
        "attachement_thumbnail",
        "attachment_thumbnail"
      ) ||
      attachment?.file_path ||
      attachment?.data_path ||
      attachment?.thumb_path ||
      attachment?.path ||
      "";
    return this.appendSupportAttachmentRevision(
      this.normalizeSupportAttachmentUrl(rawUrl),
      attachment
    );
  }

  private getSupportAttachmentSourceUrl(attachment: any) {
    const rawUrl =
      this.getSupportAttachmentField(
        attachment,
        "attachement_url",
        "attachment_url"
      ) ||
      attachment?.file_path ||
      attachment?.data_path ||
      attachment?.path ||
      "";
    return this.appendSupportAttachmentRevision(
      this.normalizeSupportAttachmentUrl(rawUrl),
      attachment
    );
  }

  getSupportAttachmentPreviewSrc(attachment: any) {
    if (this.isSupportVideoAttachment(attachment)) {
      const videoPreviewUrl =
        this.getSupportAttachmentField(
          attachment,
          "attachement_thumbnail",
          "attachment_thumbnail"
        ) ||
        attachment?.thumb_path ||
        "";
      return videoPreviewUrl
        ? this.appendSupportAttachmentRevision(
            this.normalizeSupportAttachmentUrl(videoPreviewUrl),
            attachment
          )
        : "assets/images/document-icon/video-and-audio.png";
    }

    const previewUrl =
      this.getSupportAttachmentField(
        attachment,
        "attachement_thumbnail",
        "attachment_thumbnail"
      ) ||
      attachment?.thumb_path ||
      this.getSupportAttachmentField(
        attachment,
        "attachement_url",
        "attachment_url"
      ) ||
      attachment?.file_path ||
      attachment?.data_path ||
      attachment?.path ||
      "";
    return previewUrl
      ? this.appendSupportAttachmentRevision(
          this.normalizeSupportAttachmentUrl(previewUrl),
          attachment
        )
      : "assets/images/document-icon/default.png";
  }

  getSupportAttachmentFallbackSrc(attachment: any) {
    return this.isSupportVideoAttachment(attachment)
      ? "assets/images/document-icon/video-and-audio.png"
      : "assets/images/document-icon/default.png";
  }

  isSupportImageAttachment(attachment: any) {
    const type = String(
      this.getSupportAttachmentField(
        attachment,
        "attachement_type",
        "attachment_type"
      ) ||
        attachment?.type ||
        attachment?.image_type ||
        ""
    ).toLowerCase();
    const fileName = String(
      this.getSupportAttachmentField(
        attachment,
        "attachement_name",
        "attachment_name"
      ) ||
        attachment?.name ||
        ""
    ).toLowerCase();
    const fileUrl = String(
      this.getSupportAttachmentSourceUrl(attachment)
    ).toLowerCase();

    if (type === "img" || type.startsWith("image/")) {
      return true;
    }

    return [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".heic"]
      .some((ext) => type.includes(ext) || fileName.endsWith(ext) || fileUrl.includes(ext));
  }

  isSupportVideoAttachment(attachment: any) {
    const type = String(
      this.getSupportAttachmentField(
        attachment,
        "attachement_type",
        "attachment_type"
      ) ||
        attachment?.type ||
        attachment?.image_type ||
        ""
    ).toLowerCase();
    const fileName = String(
      this.getSupportAttachmentField(
        attachment,
        "attachement_name",
        "attachment_name"
      ) ||
        attachment?.name ||
        ""
    ).toLowerCase();
    const fileUrl = String(
      this.getSupportAttachmentSourceUrl(attachment)
    ).toLowerCase();

    if (type === "vid" || type.startsWith("video/")) {
      return true;
    }

    return [".mp4", ".mov", ".avi", ".mkv", ".webm", ".m4v"].some(
      (ext) => type.includes(ext) || fileName.endsWith(ext) || fileUrl.includes(ext)
    );
  }

  hasOnlyImageAttachments(message: any) {
    const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
    if (!attachments.length) {
      return false;
    }

    return !String(message?.text || "").trim() && attachments.every((attachment: any) => this.isSupportImageAttachment(attachment));
  }

  hasOnlyVideoAttachments(message: any) {
    const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
    if (!attachments.length) {
      return false;
    }

    return !String(message?.text || "").trim() && attachments.every((attachment: any) => this.isSupportVideoAttachment(attachment));
  }

  hasOnlyFileAttachments(message: any) {
    const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
    if (!attachments.length) {
      return false;
    }

    return !String(message?.text || "").trim() && attachments.every(
      (attachment: any) =>
        !this.isSupportImageAttachment(attachment) &&
        !this.isSupportVideoAttachment(attachment)
    );
  }

  private normalizeSupportAttachmentUrl(value: any) {
    const raw = String(value || "").trim();
    if (!raw) {
      return "";
    }

    if (
      /^https?:\/\//i.test(raw) ||
      raw.startsWith("data:") ||
      raw.startsWith("blob:")
    ) {
      return raw;
    }

    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(raw)) {
      return `https://${raw.replace(/^\/+/, "")}`;
    }

    const normalizedPath = raw.startsWith("/") ? raw : `/${raw}`;
    if (normalizedPath.startsWith("/uploads/")) {
      return `https://${environment.DOMAIN}${normalizedPath}`;
    }

    return normalizedPath;
  }

  private appendSupportAttachmentRevision(url: string, attachment: any) {
    const normalizedUrl = String(url || "").trim();
    if (
      !normalizedUrl ||
      normalizedUrl.startsWith("assets/") ||
      normalizedUrl.startsWith("data:") ||
      normalizedUrl.startsWith("blob:")
    ) {
      return normalizedUrl;
    }

    const revision = String(
      this.getSupportAttachmentField(
        attachment,
        "attachment_id",
        "attachement_id"
      ) ||
        attachment?._id ||
        attachment?.created_at ||
        attachment?.updated_at ||
        attachment?.timestamp ||
        ""
    ).trim();

    if (!revision) {
      return normalizedUrl;
    }

    return `${normalizedUrl}${normalizedUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(revision)}`;
  }

  private getSupportAttachmentField(
    attachment: any,
    primaryKey: string,
    legacyKey?: string
  ) {
    if (!attachment || typeof attachment !== "object") {
      return "";
    }

    const primaryValue = attachment?.[primaryKey];
    if (
      primaryValue !== undefined &&
      primaryValue !== null &&
      String(primaryValue).trim() !== ""
    ) {
      return primaryValue;
    }

    const legacyValue = legacyKey ? attachment?.[legacyKey] : undefined;
    if (
      legacyValue !== undefined &&
      legacyValue !== null &&
      String(legacyValue).trim() !== ""
    ) {
      return legacyValue;
    }

    return "";
  }

  private normalizeSupportAttachmentPayload(attachment: any) {
    if (!attachment || typeof attachment !== "object") {
      return attachment;
    }

    const normalized = { ...attachment };
    normalized.attachement_url =
      this.getSupportAttachmentField(
        normalized,
        "attachement_url",
        "attachment_url"
      ) ||
      normalized?.file_path ||
      normalized?.data_path ||
      normalized?.path ||
      "";
    normalized.attachement_thumbnail =
      this.getSupportAttachmentField(
        normalized,
        "attachement_thumbnail",
        "attachment_thumbnail"
      ) ||
      normalized?.thumb_path ||
      "";
    normalized.attachement_name =
      this.getSupportAttachmentField(
        normalized,
        "attachement_name",
        "attachment_name"
      ) ||
      normalized?.name ||
      normalized?.file_name ||
      "";
    normalized.attachement_type =
      this.getSupportAttachmentField(
        normalized,
        "attachement_type",
        "attachment_type"
      ) ||
      normalized?.type ||
      normalized?.file_type ||
      normalized?.image_type ||
      "";
    normalized.attachement_size =
      normalized?.attachement_size ??
      normalized?.attachment_size ??
      normalized?.size ??
      null;

    return normalized;
  }

  protected normalizeStoredSupportMessageText(value: any) {
    const raw = String(value || "");
    if (!raw.trim()) {
      return "";
    }

    const normalizedHtml = raw
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>\s*<p>/gi, "\n\n")
      .replace(/<p[^>]*>/gi, "")
      .replace(/<\/p>/gi, "");

    const plainText = normalizedHtml.replace(/<[^>]+>/g, "");
    const textarea = document.createElement("textarea");
    textarea.innerHTML = plainText;

    return String(textarea.value || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  protected flattenSupportThreadHistory(issues: any[]): SupportChatItem[] {
    if (!Array.isArray(issues) || !issues.length) {
      return [];
    }

    const history: SupportChatItem[] = [];
    let lastDateKey = "";

    issues.forEach((issue: any) => {
      const issueSummary = String(issue?.issue_summary || "").trim();
      const hydratedIssueMessages = this.hydrateSupportReplyParents(
        Array.isArray(issue?.messages) ? issue.messages : []
      );
      const issueMessages = Array.isArray(issue?.messages)
        ? [...hydratedIssueMessages]
            .sort((left: any, right: any) => {
              const leftTime = new Date(
                left?.created_at ||
                  left?.createdAt ||
                  left?.message?.created_at ||
                  left?.message?.createdAt ||
                  0
              ).getTime();
              const rightTime = new Date(
                right?.created_at ||
                  right?.createdAt ||
                  right?.message?.created_at ||
                  right?.message?.createdAt ||
                  0
              ).getTime();
              return leftTime - rightTime;
            })
            .map((item: any) => this.mapSupportMessage(item))
            .filter((item: SupportChatItem) =>
              item.kind === "message"
                ? !!item.text.trim() || !!item.attachments?.length
                : true
            )
        : [];

      const hasIssueSummaryInMessages = issueMessages.some(
        (item: SupportChatItem) =>
          item.kind === "message" &&
          item.who === "me" &&
          item.text === issueSummary
      );

      history.push({
        kind: "divider",
        label: `Issue #${issue?.sequence_no || "--"} · ${this.getIssueStatusLabel(
          issue?.status
        )}`,
        sublabel: issue?.support_category || "Support",
      });

      if (issueSummary && !hasIssueSummaryInMessages) {
        const issueSummaryDate = issue?.requested_at || issue?.createdAt || null;
        const issueSummaryDateKey = this.getSupportDateKey(issueSummaryDate);
        if (issueSummaryDateKey && issueSummaryDateKey !== lastDateKey) {
          history.push({
            kind: "date-divider",
            label: this.getSupportDateLabel(issueSummaryDate),
          });
          lastDateKey = issueSummaryDateKey;
        }
        history.push({
          kind: "message",
          text: issueSummary,
          who: "me",
          time: this.getSupportTime(issue?.requested_at),
          attachments: [],
          raw: {
            created_at: issueSummaryDate,
          },
        });
      }

      issueMessages.forEach((messageItem) => {
        if (messageItem.kind !== "message") {
          history.push(messageItem);
          return;
        }
        const messageDate =
          messageItem.raw?.created_at ||
          messageItem.raw?.createdAt ||
          null;
        const messageDateKey = this.getSupportDateKey(messageDate);
        if (messageDateKey && messageDateKey !== lastDateKey) {
          history.push({
            kind: "date-divider",
            label: this.getSupportDateLabel(messageDate),
          });
          lastDateKey = messageDateKey;
        }
        history.push(messageItem);
      });
    });

    return history;
  }

  protected hydrateSupportReplyParents(messages: any[]) {
    if (!Array.isArray(messages) || !messages.length) {
      return [];
    }

    const messageMap = messages.reduce((acc: Record<string, any>, item: any) => {
      const id = String(item?._id || "").trim();
      if (id) {
        acc[id] = item;
      }
      return acc;
    }, {});

    return messages.map((item: any) => {
      if (item?.parent && item.parent?._id) {
        return item;
      }

      const parentId = String(item?.parent_id || "").trim();
      if (!parentId || !messageMap[parentId]) {
        return item;
      }

      return {
        ...item,
        parent: messageMap[parentId],
      };
    });
  }

  protected getIssueStatusLabel(status?: string) {
    const normalized = String(status || "").trim().toLowerCase();
    return normalized
      ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
      : "Open";
  }

  protected clearPendingSupportAttachments() {
    this.pendingSupportAttachments.forEach((item) => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    this.pendingSupportAttachments = [];
  }

  protected getSupportTime(value?: string | Date) {
    const date = value ? new Date(value) : new Date();
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  protected getSupportDateKey(value?: string | Date | null) {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  protected getSupportDateLabel(value?: string | Date | null) {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const now = new Date();
    const todayKey = this.getSupportDateKey(now);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const yesterdayKey = this.getSupportDateKey(yesterday);
    const dateKey = this.getSupportDateKey(date);

    if (dateKey === todayKey) {
      return "Today";
    }
    if (dateKey === yesterdayKey) {
      return "Yesterday";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  protected scrollSupportChatToBottom() {
    if (this.supportBottomScrollFrameId !== null) {
      cancelAnimationFrame(this.supportBottomScrollFrameId);
      this.supportBottomScrollFrameId = null;
    }
    if (this.supportBottomScrollTimeoutId) {
      clearTimeout(this.supportBottomScrollTimeoutId);
      this.supportBottomScrollTimeoutId = null;
    }

    this.supportBottomScrollFrameId = requestAnimationFrame(() => {
      this.supportBottomScrollFrameId = null;
      const messageContainer = document.getElementById("tpSupportMessages");
      if (!messageContainer) {
        return;
      }

      messageContainer.scrollTop = messageContainer.scrollHeight;
      this.supportBottomScrollTimeoutId = setTimeout(() => {
        messageContainer.scrollTop = messageContainer.scrollHeight;
        this.supportBottomScrollTimeoutId = null;
      }, 60);
    });
  }

  protected focusSupportReplyComposer() {
    requestAnimationFrame(() => {
      const composer = document.getElementById(
        "tpSupportReplyComposer"
      ) as HTMLInputElement | HTMLTextAreaElement | null;
      composer?.focus();
    });
  }

  onSupportReplyChange(value: string) {
    this.supportReplyMessage = this.normalizeSupportTextInput(value);
    this.supportTypingChanges$.next(
      this.supportReplyMessage.trim() ? this.supportReplyMessage : null
    );
  }

  private normalizeSupportTextInput(value: string) {
    const normalizedValue = String(value || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .slice(0, SupportChatFacade.MAX_SUPPORT_TEXT_LENGTH);

    const paragraphs = normalizedValue
      .split(/\n{2,}/)
      .slice(0, SupportChatFacade.MAX_SUPPORT_PARAGRAPHS);

    return paragraphs.join("\n\n");
  }

  protected bindSupportRoom() {
    const discussionId = String(this.supportDiscussionId || "").trim();
    if (!discussionId) {
      return;
    }

    if (this.activeSupportRoomId && this.activeSupportRoomId !== discussionId) {
      this.supportSocketService.leaveRoom(this.activeSupportRoomId);
    }

    this.activeSupportRoomId = discussionId;
    this.supportSocketService.joinRoom(discussionId);
  }

  protected leaveActiveSupportRoom() {
    this.supportTypingChanges$.next(null);
    if (!this.activeSupportRoomId) {
      return;
    }

    this.supportSocketService.leaveRoom(this.activeSupportRoomId);
    this.activeSupportRoomId = "";
  }

  protected clearSupportUnreadCount() {
    this.supportUnreadCount = 0;
  }

  protected hasActiveSupportIssue() {
    return !!this.getActiveSupportIssue(
      this.supportThreadData?.issues || [this.activeSupportIssueSession].filter(Boolean)
    );
  }

  private showSupportJoinedNoticeIfNeeded() {
    const issueId = String(this.activeSupportIssueSession?._id || "").trim();
    const joinedLabel = this.getSupportJoinedLabel();
    if (
      !issueId ||
      this.supportJoinedIssueIds.has(issueId) ||
      this.activeSupportIssueSession?.activated_at
    ) {
      return;
    }

    const joinedMessageId = `support-joined-${issueId}`;
    const existingMessageIndex = this.chatMessages.findIndex(
      (item) => item.kind === "system-divider" && item.id === joinedMessageId
    );
    const alreadyPresent = existingMessageIndex >= 0;
    if (alreadyPresent) {
      if (joinedLabel) {
        this.chatMessages = this.chatMessages.map((item, index) =>
          index === existingMessageIndex && item.kind === "system-divider"
            ? {
                ...item,
                label: joinedLabel,
              }
            : item
        );
      }
      this.supportJoinedIssueIds.add(issueId);
      return;
    }

    const currentIssue = this.supportLoadedIssues.find(
      (issue: any) => String(issue?._id || "") === issueId
    );
    const hasExistingSupportMessage = Array.isArray(currentIssue?.messages)
      ? currentIssue.messages.some((item: any) => {
          const senderId = Number(
            item?.sender_id || item?.sender?.user_id || item?.sender?.id || 0
          );
          return !!senderId && senderId !== this.supportUserId;
        })
      : false;

    if (hasExistingSupportMessage) {
      this.supportJoinedIssueIds.add(issueId);
      return;
    }

    this.supportJoinedIssueIds.add(issueId);
    this.chatMessages = [
      ...this.chatMessages,
      {
        kind: "system-divider",
        id: joinedMessageId,
        label: joinedLabel,
      },
    ];
    this.scrollSupportChatToBottom();
  }

  private getSupportJoinedLabel() {
    const responderName = String(
      this.activeSupportIssueSession?.first_replied_admin?.admin_name ||
        this.activeSupportIssueSession?.last_replied_admin?.admin_name ||
        this.supportThreadData?.first_replied_admin?.admin_name ||
        this.supportThreadData?.last_replied_admin?.admin_name ||
        ""
    ).trim();

    return responderName
      ? `${responderName} joined the chat.`
      : "TaskOPad Support joined the chat.";
  }

  private getSupportTypingLabel() {
    const responderName = String(
      this.activeSupportIssueSession?.first_replied_admin?.admin_name ||
        this.activeSupportIssueSession?.last_replied_admin?.admin_name ||
        this.supportThreadData?.first_replied_admin?.admin_name ||
        this.supportThreadData?.last_replied_admin?.admin_name ||
        ""
    ).trim();

    return responderName
      ? `${responderName} is typing...`
      : "TaskOPad Support is typing...";
  }

  private handleIncomingSupportMessage(message: any) {
    if (
      !message ||
      String(message?.support_issue_session_id || "") !==
        String(this.activeSupportIssueSession?._id || "")
    ) {
      return;
    }

    const mappedMessage = this.mapSupportMessage(message);
    const alreadyExists = this.chatMessages.some(
      (item) => item.kind === "message" && item.id === mappedMessage.id
    );
    if (alreadyExists) {
      return;
    }

    this.chatMessages = [...this.chatMessages, mappedMessage];
    if (mappedMessage.who === "agent") {
      if (this.isSupportShellOpen && this.currentSupportScreen === "chat") {
        this.clearSupportUnreadCount();
      } else {
        this.supportUnreadCount += 1;
      }
    }
    if (!this.isSupportShellOpen && mappedMessage.who === "agent") {
      this.toastrService.info("New support reply received.");
    }
    this.scrollSupportChatToBottom();
  }

  private getSupportUnreadCountFromIssue(issue: any) {
    const messages = Array.isArray(issue?.messages) ? issue.messages : [];
    if (!messages.length) {
      return 0;
    }

    let lastOwnMessageIndex = -1;
    messages.forEach((item: any, index: number) => {
      const senderId = Number(
        item?.sender_id || item?.sender?.user_id || item?.sender?.id || 0
      );
      if (senderId === this.supportUserId) {
        lastOwnMessageIndex = index;
      }
    });

    return messages.slice(lastOwnMessageIndex + 1).reduce((count: number, item: any) => {
      const senderId = Number(
        item?.sender_id || item?.sender?.user_id || item?.sender?.id || 0
      );
      return senderId && senderId !== this.supportUserId ? count + 1 : count;
    }, 0);
  }

  private async hydrateSentSupportAttachmentMessage(
    messageId: string,
    issueSession?: any,
    discussionId?: string
  ) {
    const maxAttempts = 4;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      await this.loadSupportDiscussionHistory(issueSession, discussionId);

      const sentMessage = this.chatMessages.find(
        (item: any) =>
          item?.kind === "message" && String(item?.id || "") === messageId
      );
      const attachments =
        sentMessage?.kind === "message" && Array.isArray(sentMessage?.attachments)
          ? sentMessage.attachments
          : [];

      const hasHydratedPreview = attachments.some((attachment: any) => {
        const previewSrc = String(
          this.getSupportAttachmentPreviewSrc(attachment) || ""
        ).trim();
        const sourceUrl = String(
          this.getSupportAttachmentUrl(attachment) || ""
        ).trim();
        const isVideo = this.isSupportVideoAttachment(attachment);
        const isImage = this.isSupportImageAttachment(attachment);

        if (isVideo || isImage) {
          return !!previewSrc && !previewSrc.startsWith("assets/images/document-icon/");
        }

        return !!sourceUrl;
      });

      if (!attachments.length || hasHydratedPreview || attempt === maxAttempts) {
        return;
      }

      await this.waitForSupportAttachmentHydration(700);
    }
  }

  private waitForSupportAttachmentHydration(delayMs: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });
  }
}
